import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Container } from "@mui/material";
import axios from "axios";
import {
  AccountBalance,
  AttachMoney,
  Business,
  CardTravel,
  Computer,
  Gavel,
  HomeWork,
  LocalHospital,
  School,
  Science,
  Store,
} from "@mui/icons-material";

import Banner from "../components/home/Banner";
import Industries from "../components/home/Industries";
import Associations from "../components/home/Associations";
import NewsEvents from "../components/home/NewsEvents/NewsEvents";
import CommunityMeetups from "../components/home/CommunityMeetups";
import "../styles/home.css";

const industries = [
  { label: "Insurance", icon: <Business />, route: "insurance", color: "#1976d2" },
  { label: "Healthcare", icon: <LocalHospital />, route: "healthcare", color: "#e53935" },
  { label: "Education", icon: <School />, route: "education", color: "#8e24aa" },
  { label: "Small Industries", icon: <Store />, route: "small-industries", color: "#f57c00" },
  { label: "Research", icon: <Science />, route: "research", color: "#43a047" },
  { label: "Travel", icon: <CardTravel />, route: "travel", color: "#00acc1" },
  { label: "Real Estate", icon: <HomeWork />, route: "real-estate", color: "#6d4c41" },
  { label: "Finance", icon: <AttachMoney />, route: "finance", color: "#2e7d32" },
  { label: "Banking", icon: <AccountBalance />, route: "banking", color: "#3949ab" },
  { label: "Legal", icon: <Gavel />, route: "legal", color: "#5e35b1" },
  { label: "IT", icon: <Computer />, route: "it", color: "#546e7a" },
];

const homeTutorialSteps = [
  {
    eyebrow: "Start here",
    title: "What brought you here today?",
    body: "Begin with the UC Opportunity Companion prompt. Choose the kind of help, opportunity, internship, or connection you are looking for — no login is needed to explore.",
    selector: "[data-tour='uc-intent-entry']",
    placement: "bottom",
  },
  {
    eyebrow: "Your guide",
    title: "Find Your Opportunity & Impact",
    body: "The blue floating UC Companion stays available throughout the site. Use it for a focused opportunity assessment, personal guidance, or a referral path.",
    selector: "[data-tour='uc-companion-fab']",
    placement: "left",
  },
  {
    eyebrow: "Stay connected",
    title: "Build your UC profile",
    body: "Create a profile or sign in when you want UC to save your progress, keep your reports and registrations together, and connect you with the right people.",
    selector: "[data-tour='nav-profile'], [data-tour='nav-login']",
    placement: "bottom",
  },
  {
    eyebrow: "Explore pathways",
    title: "Find a sector and next step",
    body: "Use the industry cards to explore practical opportunity pathways, community resources, and the people or programs that can help you move forward.",
    selector: "[data-tour='home-industries']",
    placement: "top",
  },
  {
    eyebrow: "Meet the community",
    title: "Community meetups and events",
    body: "Open the Calendar to find UC community meetups, public sessions, learning programs, and registration links near you or online.",
    selector: "[data-tour='nav-calendar']",
    placement: "bottom",
  },
  {
    eyebrow: "More support",
    title: "Divine Rituals help",
    body: "The gold OM floating assistant helps families find a Pandit or book a puja or ritual at a suitable muhurta. It appears at intervals and can always be dismissed.",
    selector: "[data-tour='divine-rituals-fab']",
    placement: "left",
  },
];

const Home = () => {
  const location = useLocation();
  const [associations, setAssociations] = useState([]);
  const [associationStatus, setAssociationStatus] = useState("loading");
  const [showTutorial, setShowTutorial] = useState(() => {
    return localStorage.getItem("udyog2HomeTutorialSeen") !== "yes";
  });
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialTarget, setTutorialTarget] = useState(null);

  useEffect(() => {
    let mounted = true;

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/getassociation`)
      .then((res) => {
        if (!mounted) return;
        setAssociations(Array.isArray(res.data) ? res.data : []);
        setAssociationStatus("ready");
      })
      .catch(() => {
        if (!mounted) return;
        setAssociations([]);
        setAssociationStatus("error");
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const openTour = () => {
      setTutorialStep(0);
      setShowTutorial(true);
    };

    window.addEventListener("udyog2-open-home-tour", openTour);

    if (location.state?.openTutorial) {
      openTour();
    }

    return () => window.removeEventListener("udyog2-open-home-tour", openTour);
  }, [location.state]);

  const closeTutorial = () => {
    localStorage.setItem("udyog2HomeTutorialSeen", "yes");
    window.dispatchEvent(new Event("udyog2-close-tour-explore"));
    setShowTutorial(false);
    setTutorialStep(0);
  };

  const completeTutorial = () => {
    closeTutorial();
    // Hand the visitor directly to the companion after the concise site tour.
    // This is intentionally dispatched only when the visitor finishes, not when
    // they skip or close the guide.
    window.setTimeout(() => window.dispatchEvent(new Event("udyog2-open-intent-prompt")), 180);
  };

  const activeTutorial = homeTutorialSteps[tutorialStep];
  const isLastTutorialStep = tutorialStep === homeTutorialSteps.length - 1;

  useEffect(() => {
    if (!showTutorial) return undefined;

    const updateTarget = () => {
      const isExploreStep = activeTutorial.selector.includes("nav-events") ||
        activeTutorial.selector.includes("nav-companies") ||
        activeTutorial.selector.includes("nav-entry") ||
        activeTutorial.selector.includes("nav-techsummit");

      if (
        activeTutorial.selector.includes("nav-") &&
        !activeTutorial.selector.includes("nav-profile") &&
        window.innerWidth <= 768
      ) {
        window.dispatchEvent(new Event("udyog2-open-tour-menu"));
      }

      if (isExploreStep) {
        window.dispatchEvent(new Event("udyog2-open-tour-explore"));
      } else {
        window.dispatchEvent(new Event("udyog2-close-tour-explore"));
      }

      const target = document.querySelector(activeTutorial.selector);

      if (!target) {
        setTutorialTarget(null);
        return;
      }

      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });

      window.setTimeout(() => {
        const rect = target.getBoundingClientRect();
        const isVisible =
          rect.width > 0 &&
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.right > 0 &&
          rect.top < window.innerHeight &&
          rect.left < window.innerWidth;

        setTutorialTarget(isVisible ? rect : null);
      }, 180);
    };

    updateTarget();
    window.addEventListener("resize", updateTarget);
    window.addEventListener("scroll", updateTarget, true);

    return () => {
      window.removeEventListener("resize", updateTarget);
      window.removeEventListener("scroll", updateTarget, true);
      window.dispatchEvent(new Event("udyog2-close-tour-explore"));
    };
  }, [activeTutorial, showTutorial]);

  const getCalloutStyle = () => {
    if (!tutorialTarget) return {};

    const gap = 16;
    const cardWidth = Math.min(360, window.innerWidth - 32);
    const cardHeight = Math.min(380, window.innerHeight - 32);
    const targetCenter = tutorialTarget.left + tutorialTarget.width / 2;
    const left = Math.min(
      Math.max(16, targetCenter - cardWidth / 2),
      window.innerWidth - cardWidth - 16
    );
    const safeTop = (value) =>
      Math.min(Math.max(16, value), window.innerHeight - cardHeight - 16);

    if (activeTutorial.placement === "top") {
      return {
        width: cardWidth,
        left,
        top:
          tutorialTarget.top - cardHeight - gap > 16
            ? tutorialTarget.top - cardHeight - gap
            : safeTop(tutorialTarget.bottom + gap),
        transform: "none",
      };
    }

    if (activeTutorial.placement === "left") {
      const hasLeftSpace = tutorialTarget.left - cardWidth - gap > 16;
      return {
        width: cardWidth,
        left: hasLeftSpace ? tutorialTarget.left - cardWidth - gap : left,
        top: safeTop(tutorialTarget.top),
      };
    }

    const preferredBottomTop = tutorialTarget.bottom + gap;
    const hasBottomSpace = preferredBottomTop + cardHeight < window.innerHeight - 16;
    return {
      width: cardWidth,
      left,
      top: hasBottomSpace
        ? preferredBottomTop
        : safeTop(tutorialTarget.top - cardHeight - gap),
    };
  };

  return (
    <Box className="home-page" data-tour="home-entry">
      {showTutorial && (
        <div className="udyog-tour-layer" role="dialog" aria-modal="true" aria-labelledby="udyog-tour-title">
          {tutorialTarget && (
            <div
              className="udyog-tour-highlight"
              style={{
                left: tutorialTarget.left - 8,
                top: tutorialTarget.top - 8,
                width: tutorialTarget.width + 16,
                height: tutorialTarget.height + 16,
              }}
            />
          )}
          <div
            className={`udyog-tour-card ${tutorialTarget ? "anchored" : "centered"} placement-${activeTutorial.placement}`}
            style={getCalloutStyle()}
          >
            <button
              className="udyog-tour-close"
              type="button"
              onClick={closeTutorial}
              aria-label="Skip tutorial"
            >
              x
            </button>
            <div className="udyog-tour-step">
              Step {tutorialStep + 1} of {homeTutorialSteps.length}
            </div>
            <span className="udyog-tour-kicker">{activeTutorial.eyebrow}</span>
            <h2 id="udyog-tour-title">{activeTutorial.title}</h2>
            <p>{activeTutorial.body}</p>
            <div className="udyog-tour-progress">
              {homeTutorialSteps.map((step, index) => (
                <button
                  key={step.title}
                  type="button"
                  className={`udyog-tour-dot ${index === tutorialStep ? "active" : ""}`}
                  onClick={() => setTutorialStep(index)}
                  aria-label={`Go to tutorial step ${index + 1}`}
                />
              ))}
            </div>
            <div className="udyog-tour-actions">
              <button type="button" className="udyog-tour-secondary" onClick={closeTutorial}>
                Skip
              </button>
              <div className="udyog-tour-nav">
                <button
                  type="button"
                  className="udyog-tour-secondary"
                  disabled={tutorialStep === 0}
                  onClick={() => setTutorialStep((step) => Math.max(step - 1, 0))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="udyog-tour-primary"
                  onClick={() => {
                    if (isLastTutorialStep) {
                      completeTutorial();
                    } else {
                      setTutorialStep((step) => step + 1);
                    }
                  }}
                >
                  {isLastTutorialStep ? "Start Exploring" : "Next"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Banner />

      <Container maxWidth="xl" className="home-main">
        <CommunityMeetups />

        <Box data-tour="home-news">
          <NewsEvents />
        </Box>

        <Box className="home-flow-grid">
          <Box data-tour="home-industries">
            <Industries industries={industries} />
          </Box>
          <Box data-tour="home-associations">
            <Associations associations={associations} status={associationStatus} />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Home;
